import React, { FC, useEffect, useState } from 'react';
import { Autocomplete, Popper, TextField } from '@mui/material';
import { DataTableAutocompleteOption } from './data-table-autocomplete.component';

/**
 * The code cell of the action and material grids (DD-2506).
 *
 * It shows the row's own code whether or not the code is in the catalogue: a row can be a
 * frozen snapshot of a catalogue article, a migrated row whose article has since changed,
 * or a free-text row. The dropdown is only a way to replace the whole article: picking an
 * option hands it to `onSelectValue`, and the row applies it in full (code, description,
 * prices). Focusing the cell clears the text so the list can be filtered; leaving it
 * without picking an option puts the row's code back, so nothing is lost by opening it.
 */
type DataTableCodeAutocompleteProps = {
    code: string;
    options: DataTableAutocompleteOption[];
    onSelectValue: (option: DataTableAutocompleteOption) => void;
    selected?: boolean;
    disabled?: boolean;
    error?: boolean;
    width?: string | number;
    onFocus?: () => void;
    onBlur?: () => void;
};

const normalizeRegex = /[^a-z0-9]/gi;
const normalize = (text: string) => text.toLowerCase().replace(normalizeRegex, '');

const backgroundColor = (props: DataTableCodeAutocompleteProps): string => {
    if (props.error) return '#fdecea';
    if (props.disabled) return '#f7f7f7';
    if (props.selected) return '#e9f5f3';
    return 'transparent';
};

/** Enter moves to the next input of the table, like the other grid cells. */
const focusNextInput = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Enter') return;
    const table = (event.currentTarget as HTMLElement).closest('table');
    if (!table) return;
    const focusable = Array.from(
        table.querySelectorAll<HTMLElement>("input, select, textarea, [tabindex]:not([tabindex='-1'])"),
    ).filter((element) => !element.hasAttribute('disabled'));
    const next = focusable[focusable.indexOf(document.activeElement as HTMLElement) + 1];
    next?.focus();
};

const DataTableCodeAutocomplete: FC<DataTableCodeAutocompleteProps> = (props) => {
    const { code, options, onSelectValue, disabled, error, width, onFocus, onBlur } = props;
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState(code);

    // The row owns the code; while the user is not typing, the cell shows it.
    useEffect(() => {
        if (!editing) setInputValue(code);
    }, [code, editing]);

    return (
        <Autocomplete<DataTableAutocompleteOption, false, true, false>
            value={null as unknown as DataTableAutocompleteOption}
            inputValue={inputValue}
            disabled={disabled}
            options={options || []}
            openOnFocus
            blurOnSelect
            clearOnBlur={false}
            disableClearable
            autoHighlight
            onFocus={() => {
                setEditing(true);
                setInputValue('');
                onFocus?.();
            }}
            onBlur={() => {
                setEditing(false);
                setInputValue(code);
                onBlur?.();
            }}
            onInputChange={(_, value, reason) => {
                // Only typing filters the list; MUI's own resets (on select, escape, blur)
                // must not overwrite the code the row carries.
                if (reason === 'input') setInputValue(value);
            }}
            onChange={(_, option) => {
                if (option) {
                    setEditing(false);
                    onSelectValue(option);
                }
            }}
            filterOptions={(all, state) => {
                const query = normalize(state.inputValue);
                return all.filter(
                    (option) => normalize(option.key).includes(query) || normalize(option.value || '').includes(query),
                );
            }}
            getOptionLabel={(option) => option?.key || option?.value || ''}
            isOptionEqualToValue={(option, value) => option.id == value.id && option.key === value.key}
            renderOption={(optionProps, option) => (
                <li
                    style={{ fontSize: '0.9rem' }}
                    {...{ ...optionProps, key: `${option?.id}-${option?.key}-${option?.value}` }}
                >
                    {option.value ? `${option?.key} - ${option?.value}` : option?.key}
                </li>
            )}
            PopperComponent={(popperProps) => (
                <Popper
                    {...popperProps}
                    placement="bottom-start"
                    style={{ ...popperProps.style, width, minWidth: width }}
                />
            )}
            onKeyUp={focusNextInput}
            renderInput={(params) => (
                <TextField
                    {...params}
                    error={error}
                    inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                    sx={{
                        '& .MuiInputBase-root': {
                            padding: 0,
                            fontSize: '0.9rem',
                            backgroundColor: backgroundColor(props),
                        },
                        '& .MuiOutlinedInput-root': {
                            padding: '0 !important',
                            margin: '0 !important',
                            '& fieldset': { border: 'none' },
                            fontSize: '0.9rem',
                        },
                        border: 'none',
                        padding: '0 !important',
                        margin: '0 !important',
                    }}
                />
            )}
        />
    );
};

export default DataTableCodeAutocomplete;
